import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

/**
 * SimpleTable: Cleaner, simpler table for line items (like expenses)
 * More streamlined than NestedExpandable - no borders between cells, cleaner design
 * 
 * Props:
 * - field: {
 *     name: string,
 *     label?: string,
 *     helperText?: string,
 *     addButtonLabel?: string,
 *     columns: Array<{ 
 *       name: string, 
 *       label: string, 
 *       type?: 'text'|'number', 
 *       width?: string|number,
 *       align?: 'left'|'right'|'center',
 *       readonly?: boolean,
 *       placeholder?: string
 *     }>
 *   }
 * - formData: object
 * - setFormData: function
 * - readOnly: boolean
 */
export default function SimpleTable(props) {
  const fieldName = props.field.name;
  const rows = props.formData?.[fieldName] || [];
  const columns = props.field.columns || [];

  const buildEmptyRow = () => {
    const row = {};
    columns.forEach((col) => {
      row[col.name] = col.type === 'number' ? '' : '';
    });
    return row;
  };

  const addRow = () => {
    props.setFormData((prev) => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), buildEmptyRow()],
    }));
  };

  const deleteRow = (index) => {
    props.setFormData((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index),
    }));
  };

  const updateCell = (rowIndex, colName, value) => {
    props.setFormData((prev) => {
      const updated = [...(prev[fieldName] || [])];
      updated[rowIndex] = { ...updated[rowIndex], [colName]: value };
      return { ...prev, [fieldName]: updated };
    });
  };

  const renderCell = (rowIndex, col, value) => {
    const isReadOnly = Boolean(col.readonly) || props.readOnly;
    const textAlign = col.align || 'left';

    const commonProps = {
      id: `field-${fieldName}-${rowIndex}-${col.name}`,
      fullWidth: true,
      size: 'small',
      variant: 'standard',
      value: value ?? '',
      onChange: (e) => updateCell(rowIndex, col.name, e.target.value),
      placeholder: col.placeholder,
      InputProps: { 
        readOnly: isReadOnly,
        disableUnderline: true,
      },
      slotProps: {
        htmlInput: {
          id: `field-${fieldName}-${rowIndex}-${col.name}`,
          name: `${fieldName}.${rowIndex}.${col.name}`,
        }
      },
      sx: {
        '& input': { 
          textAlign,
          padding: '8px 12px',
        },
        '& .MuiInputBase-input': { textAlign },
      },
    };

    if (col.type === 'number') {
      return <TextField type="number" {...commonProps} />;
    }
    return <TextField {...commonProps} />;
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      {props.field.label && (
        <Typography variant="h6" gutterBottom>
          {props.field.label}
        </Typography>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: 'none',
          backgroundColor: 'transparent',
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.name}
                  align={col.align || 'left'}
                  sx={{
                    width: col.width || 'auto',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderBottom: 'none',
                    paddingTop: 1,
                    paddingBottom: 1,
                    backgroundColor: 'transparent',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{
                  width: 80,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderBottom: 'none',
                  paddingTop: 1,
                  paddingBottom: 1,
                  backgroundColor: 'transparent',
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  align="center"
                  sx={{ 
                    py: 2, 
                    color: 'text.secondary',
                    borderBottom: 'none',
                  }}
                >
                  No rows yet. Use "Add Row" to start.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rIdx) => (
                <TableRow
                  key={rIdx}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.name}
                      align={col.align || 'left'}
                      sx={{
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        padding: 0,
                        verticalAlign: 'middle',
                      }}
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 1 }}>
                        {col.name === columns[0]?.name && (
                          <MoreHorizIcon fontSize="small" sx={{ color: 'action.disabled' }} />
                        )}
                        {renderCell(rIdx, col, row?.[col.name])}
                      </Stack>
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{
                      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                      padding: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deleteRow(rIdx)}
                      disabled={props.readOnly}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {props.field.helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
          {props.field.helperText}
        </Typography>
      )}

      {!props.readOnly && (
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={addRow}
          sx={{ mt: 1, boxShadow: 0 }}
        >
          {props.field.addButtonLabel || 'Add Row'}
        </Button>
      )}
    </Box>
  );
}
